import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	assignRole,
	assignTemplate,
	getRoles,
	getRoleTemplates,
	getUserAssignments,
	removeRole,
	toggleUserFlag,
} from "../api/rolesPermissionsApi";
import { ROLES_KEYS } from "./useRoles";
import type {
	AssignRolePayload,
	FlagTogglePayload,
} from "../types/RolesPermissions.type";

export const USER_ASSIGNMENTS_KEY = "rbac-user-assignments";
// FF-W3-3: share 012's canonical roles-list cache key (was "rbac-roles") so 012's clone/rename/delete
// mutations (which invalidate ROLES_KEYS.list()) also refresh the assign wizard/drawer role list.
export const ROLES_KEY = ROLES_KEYS.list();
export const ROLE_TEMPLATES_KEY = "rbac-role-templates";

// `enabled` lets callers on the shared (RBAC-off) user surface skip the RBAC
// assignments request entirely — PRO-413: UserListPage renders in both flag
// states and must not fire this query when RBAC v2 is off.
export const useUserAssignments = ({ enabled = true }: { enabled?: boolean } = {}) => {
	const { data, isLoading, error } = useQuery({
		queryKey: [USER_ASSIGNMENTS_KEY],
		queryFn: getUserAssignments,
		enabled,
	});

	return { users: data ?? [], isLoading, error };
};

export const useRoles = () => {
	// Keys on the canonical ROLES_KEYS.list() (FF-W3-3) but keeps 013's own robust unwrap queryFn.
	const { data, isLoading, error } = useQuery({
		queryKey: ROLES_KEY,
		queryFn: getRoles,
	});

	return { roles: data ?? [], isLoading, error };
};

export const useRoleTemplates = () => {
	const { data, isLoading, error } = useQuery({
		queryKey: [ROLE_TEMPLATES_KEY],
		queryFn: getRoleTemplates,
	});

	return { templates: data ?? [], isLoading, error };
};

// Mutations invalidate the assignments list so rows reflect server-authoritative state (AC5/AC6).
export const useAssignmentMutations = () => {
	const queryClient = useQueryClient();
	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: [USER_ASSIGNMENTS_KEY] });

	const assignRoleMutation = useMutation({
		mutationFn: ({ userId, payload }: { userId: number; payload: AssignRolePayload }) =>
			assignRole(userId, payload),
		onSuccess: invalidate,
	});

	const removeRoleMutation = useMutation({
		mutationFn: ({ userId, assignmentId }: { userId: number; assignmentId: number }) =>
			removeRole(userId, assignmentId),
		onSuccess: invalidate,
	});

	const assignTemplateMutation = useMutation({
		mutationFn: ({ userId, templateId }: { userId: number; templateId: number }) =>
			assignTemplate(userId, templateId),
		onSuccess: invalidate,
	});

	const toggleFlagMutation = useMutation({
		mutationFn: ({ userId, payload }: { userId: number; payload: FlagTogglePayload }) =>
			toggleUserFlag(userId, payload),
		onSuccess: invalidate,
	});

	return {
		assignRoleMutation,
		removeRoleMutation,
		assignTemplateMutation,
		toggleFlagMutation,
	};
};
